import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { trackRequestCreated } from '../../utils/mixpanel';

const requestTypeQuestions: Record<string, { label: string; name: string; type?: string; required?: boolean; options?: string[]; showIf?: { field: string; value: string | string[] }; description?: string }[]> = {
  'graphic-design': [
    { label: 'What type of graphic design do you need?', name: 'type', type: 'select', required: true, options: ['Logo', 'Social Post', 'Flyer', 'Poster', 'Infographic'] },
    { label: 'Describe your project', name: 'description', required: true },
    { label: 'Do you have brand assets?', name: 'brand_assets', type: 'brand_assets_upload', required: false, description: 'Upload your logo, brand guidelines, color palettes, and other brand elements' },
    { label: 'Upload inspiration images or examples', name: 'inspirations', type: 'inspiration_upload', required: false, description: 'Upload images that inspire your design vision' },
    // Conditional questions for Logo
    { label: 'Do you want a text-based logo, icon-based, or a combination?', name: 'logo_style', type: 'select', required: false, options: ['Text-based', 'Icon-based', 'Combination'], showIf: { field: 'type', value: 'Logo' } },
    { label: 'Should the logo be modern, vintage, minimal, abstract, etc.?', name: 'logo_aesthetic', type: 'text', required: false, showIf: { field: 'type', value: 'Logo' } },
    { label: 'Where will you use the logo? (Print, web, app, merch)', name: 'logo_usage', type: 'textarea', required: false, showIf: { field: 'type', value: 'Logo' } },
    // Conditional questions for Social Post
    { label: 'What platforms? (Instagram, LinkedIn, Facebook, TikTok, etc.)', name: 'social_platforms', type: 'textarea', required: false, showIf: { field: 'type', value: 'Social Post' } },
    { label: 'What size(s) or formats do you need?', name: 'social_sizes', type: 'text', required: false, showIf: { field: 'type', value: 'Social Post' } },
    { label: 'What kind of content: quote templates, product promos, event banners?', name: 'social_content_type', type: 'text', required: false, showIf: { field: 'type', value: 'Social Post' } },
    // Conditional questions for Flyer/Poster
    { label: 'What is the main message?', name: 'flyer_message', type: 'textarea', required: false, showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    { label: 'Is this for print or digital distribution?', name: 'flyer_distribution', type: 'select', required: false, options: ['Print', 'Digital', 'Both'], showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    { label: 'What size/dimensions?', name: 'flyer_dimensions', type: 'text', required: false, showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    { label: 'Do you need QR codes, contact info, or social handles included?', name: 'flyer_elements', type: 'textarea', required: false, showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    { label: 'Any required logos, text, or imagery?', name: 'flyer_requirements', type: 'textarea', required: false, showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    // Common questions for all types
    { label: 'What file formats do you need? (PDF, PNG, PSD, SVG, etc.)', name: 'file_formats', type: 'textarea', required: true },
    { label: 'Do you have a website or able to upload your inspiration?', name: 'inspiration_source', type: 'text', required: false },
    { label: 'Do you have any competitors?', name: 'competitors', type: 'textarea', required: false },
  ],
  'web-design': [
    { label: 'What type of web design do you need?', name: 'type', type: 'select', required: true, options: ['Landing Page', 'Microsite', 'Website Mockup'] },
    { label: 'What is the purpose of the website or landing page? (e.g., lead generation, product launch, portfolio, sales)', name: 'website_purpose', type: 'select', required: true, options: ['Lead Generation', 'Product Launch', 'Portfolio', 'Sales', 'Information', 'Other'] },
    { label: 'Who is your target audience?', name: 'target_audience', type: 'textarea', required: true },
    { label: 'What action should users take? (Call, sign up, purchase, download?)', name: 'user_action', type: 'text', required: true },
    { label: 'Do you have a sitemap or list of sections you want? (e.g., Hero, Features, Testimonials, Pricing, Contact)', name: 'sitemap_sections', type: 'textarea', required: true },
    { label: 'Do you have all the copy (text), or will you need help writing it?', name: 'copy_status', type: 'select', required: true, options: ['All copy ready', 'Need help writing', 'Mix of both'] },
    { label: 'Do you have photos, illustrations, or branding assets ready?', name: 'assets_status', type: 'select', required: true, options: ['All assets ready', 'Need some assets', 'Need all assets'] },
    { label: 'Do you have brand guidelines (colors, fonts, logos)?', name: 'brand_guidelines', type: 'select', required: true, options: ['Yes, complete guidelines', 'Partial guidelines', 'No guidelines'] },
    { label: 'Are there websites or landing pages you love or hate? Why?', name: 'reference_websites', type: 'textarea', required: false },
    { label: 'Upload inspiration images or examples', name: 'inspirations', type: 'inspiration_upload', required: false, description: 'Upload screenshots or images that inspire your website design' },
  ],
  'ui-ux-design': [
    { label: 'What type of UI/UX design do you need?', name: 'type', type: 'select', required: true, options: ['App Flow', 'Wireframe', 'User Journey'] },
    { label: 'What platform is this for?', name: 'platform', required: true },
    { label: 'Describe the user journey', name: 'user_journey' },
    { label: 'Upload inspiration images or examples', name: 'inspirations', type: 'inspiration_upload', required: false, description: 'Upload app screenshots or designs that inspire your UI/UX' },
  ],
  'branding': [
    { label: 'What type of branding do you need?', name: 'type', type: 'select', required: true, options: ['Brand Guide', 'Visual Identity', 'Assets'] },
    { label: 'What\'s your business name, and what does it mean?', name: 'business_name_meaning', type: 'textarea', required: true },
    { label: 'What products or services do you offer?', name: 'products_services', type: 'textarea', required: true },
    { label: 'Who is your ideal customer or audience?', name: 'ideal_customer', type: 'textarea', required: true },
    { label: 'How would you describe your brand in 3–5 words?', name: 'brand_description', type: 'text', required: true },
    { label: 'What brands do you admire and why?', name: 'admired_brands', type: 'textarea', required: false },
    { label: 'Do you have a color palette in mind or want suggestions?', name: 'color_palette', type: 'textarea', required: false },
    { label: 'Are there any colors, fonts, symbols, or imagery you want to avoid?', name: 'avoid_elements', type: 'textarea', required: false },
    { label: 'Do you have any existing branding (logo, colors, fonts) we should build on or discard?', name: 'existing_branding', type: 'textarea', required: false },
    { label: 'Do you prefer a modern, vintage, minimalist, bold, or elegant look?', name: 'brand_style', type: 'select', required: true, options: ['Modern', 'Vintage', 'Minimalist', 'Bold', 'Elegant', 'Other'] },
    { label: 'Upload inspiration images or examples', name: 'inspirations', type: 'inspiration_upload', required: false, description: 'Upload logos, designs, or images that inspire your brand vision' },
  ],
  'illustration': [
    { label: 'What type of illustration do you need?', name: 'type', type: 'select', required: true, options: ['Custom Illustration', 'Icon', 'Asset'] },
    { label: 'What are the illustrations/icons for? (App, website, presentation, print, etc.)', name: 'purpose', type: 'textarea', required: true },
    { label: 'How many illustrations or icons do you need?', name: 'quantity', type: 'number', required: true },
    { label: 'Where will they appear (e.g., dark background, light background, both)?', name: 'background_context', type: 'text', required: true },
    { label: 'Do you have a preferred style? (Flat, outline, filled, hand-drawn, isometric, 3D, etc.)', name: 'style_preference', type: 'select', required: true, options: ['Flat', 'Outline', 'Filled', 'Hand-drawn', 'Isometric', '3D', 'Other'] },
    { label: 'Any reference examples or design inspiration you like?', name: 'references', type: 'textarea', required: false },
    { label: 'Should they match an existing brand or design system?', name: 'brand_match', type: 'text', required: false },
    { label: 'Do you want them colorful, monochrome?', name: 'color_scheme', type: 'select', required: true, options: ['Colorful', 'Monochrome', 'Both'] },
    { label: 'What dimensions or resolution do you need? (e.g., 24x24px, 512x512px)', name: 'dimensions', type: 'text', required: true },
    { label: 'What file formats do you require? (SVG, PNG, AI, EPS, etc.)', name: 'file_formats', type: 'textarea', required: true },
    { label: 'Upload inspiration images or examples', name: 'inspirations', type: 'inspiration_upload', required: false, description: 'Upload illustrations or designs that inspire your vision' },
  ],
  'presentation-design': [
    { label: 'What type of presentation do you need?', name: 'type', type: 'select', required: true, options: ['Pitch Deck', 'Slide', 'Keynote', 'PowerPoint'] },
    { label: 'What is the topic of your presentation?', name: 'topic', required: true },
    { label: 'How many slides?', name: 'slides', type: 'number' },
    { label: 'Upload inspiration images or examples', name: 'inspirations', type: 'inspiration_upload', required: false, description: 'Upload presentation slides or designs that inspire your vision' },
  ],
  'web-development': [
    { label: 'What type of web development do you need?', name: 'type', type: 'select', required: true, options: ['Front-end', 'Landing Page', 'Integration'] },
    // 🧠 1. Vision & Purpose
    { label: 'What problem does the app solve?', name: 'problem', type: 'textarea', required: true },
    { label: 'Who is the target user?', name: 'target_user', type: 'textarea', required: true },
    { label: 'What is the main goal of the app (MVP)?', name: 'main_goal', type: 'textarea', required: true },
    { label: 'Are there similar apps you like/dislike? Why?', name: 'similar_apps', type: 'textarea' },
    // 📱 2. Features & Functionality
    { label: 'What are the core features you want in version 1?', name: 'core_features', type: 'textarea', required: true },
    { label: 'Any specific user flows or use cases you\'ve mapped out?', name: 'user_flows', type: 'textarea' },
    { label: 'Do you need user authentication? (Email, social logins, etc.)', name: 'user_auth', type: 'text' },
    { label: 'Will there be any in-app payments or subscriptions?', name: 'payments', type: 'text' },
    { label: 'Are there any integrations (APIs, third-party services)?', name: 'integrations', type: 'textarea' },
    // 🎨 3. Design & User Experience
    { label: 'Do you already have branding or UI/UX designs?', name: 'branding_uiux', type: 'text' },
    { label: 'Are you open to a custom UI or want to follow platform guidelines (Material, iOS HIG)?', name: 'ui_guidelines', type: 'text' },
    { label: 'What are your design inspirations (apps, websites)?', name: 'design_inspirations', type: 'textarea' },
    { label: 'Upload inspiration images or examples', name: 'inspirations', type: 'inspiration_upload', required: false, description: 'Upload app screenshots or designs that inspire your development' },
  ],
  'email-development': [
    { label: 'What type of email development do you need?', name: 'type', type: 'select', required: true, options: ['Custom Template', 'Responsive Template'] },
    { label: 'What is the primary goal of this email? (e.g. promotion, newsletter, onboarding, announcement)', name: 'email_goal', type: 'select', required: true, options: ['Promotion', 'Newsletter', 'Onboarding', 'Announcement', 'Other'] },
    { label: 'Who is the target audience?', name: 'target_audience', type: 'textarea', required: true },
    { label: 'Do you have brand guidelines or existing email templates?', name: 'brand_guidelines', type: 'text', required: false },
    { label: 'Are there preferred colors, fonts, or visual styles?', name: 'visual_preferences', type: 'textarea', required: false },
    { label: 'Any reference emails or competitors you like the look of?', name: 'references', type: 'textarea', required: false },
    { label: 'What sections should be included? (e.g., hero image, headline, body, CTA, footer)', name: 'email_sections', type: 'textarea', required: true },
    { label: 'Do you have the copy and images ready?', name: 'content_ready', type: 'select', required: true, options: ['Yes, all content is ready', 'Partially ready', 'Need placeholder content'] },
    { label: 'Do you need placeholder content for now?', name: 'placeholder_content', type: 'checkbox', required: false },
    { label: 'Will there be personalized fields (e.g., "Hi [First Name]")?', name: 'personalization', type: 'textarea', required: false },
    { label: 'Upload inspiration images or examples', name: 'inspirations', type: 'inspiration_upload', required: false, description: 'Upload email designs or templates that inspire your vision' },
  ],
};

const typeTitles: Record<string, string> = {
  'graphic-design': 'Graphic Design',
  'web-design': 'Web Design',
  'ui-ux-design': 'UI/UX Design',
  'branding': 'Branding',
  'illustration': 'Illustration',
  'presentation-design': 'Presentation Design',
  'web-development': 'Web Development',
  'email-development': 'Email Development',
};

const RequestForm: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const { user } = useAuth();
  const questions = requestTypeQuestions[type || ''] || [];
  const [form, setForm] = useState<Record<string, any>>({});
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brands, setBrands] = useState<any[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inspirationFiles, setInspirationFiles] = useState<File[]>([]);
  const [brandAssetsFiles, setBrandAssetsFiles] = useState<File[]>([]);
  const [uploadingInspirations, setUploadingInspirations] = useState(false);
  const [uploadingBrandAssets, setUploadingBrandAssets] = useState(false);

  // Form persistence with localStorage
  const formStorageKey = `request-form-${type}-${user?.id}`;
  const filesStorageKey = `request-files-${type}-${user?.id}`;

  // Load saved form data on component mount
  useEffect(() => {
    if (!user || !type) return;
    
    try {
      // Load saved form data
      const savedForm = localStorage.getItem(formStorageKey);
      if (savedForm) {
        const parsedForm = JSON.parse(savedForm);
        console.log('📝 Restoring saved form data:', parsedForm);
        setForm(parsedForm);
        
        // Show a subtle notification that form data was restored
        if (Object.keys(parsedForm).length > 0) {
          // You can add a toast notification here if you have a notification system
          console.log('✅ Form data restored from previous session');
        }
      }
      
      // Note: File objects can't be stored in localStorage, so we only restore form fields
      // Files will need to be re-uploaded if the user switches tabs
    } catch (error) {
      console.error('Error loading saved form data:', error);
      localStorage.removeItem(formStorageKey);
    }
  }, [user, type, formStorageKey]);

  // Save form data whenever it changes
  useEffect(() => {
    if (!user || !type || Object.keys(form).length === 0) return;
    
    try {
      // Save form data to localStorage
      localStorage.setItem(formStorageKey, JSON.stringify(form));
      console.log('💾 Saving form data to localStorage');
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }, [form, user, type, formStorageKey]);

  // Clear saved form data on successful submission
  const clearSavedForm = () => {
    localStorage.removeItem(formStorageKey);
    localStorage.removeItem(filesStorageKey);
    console.log('🧹 Cleared saved form data after successful submission');
  };

  // Add a warning about file persistence
  const showFileWarning = () => {
    if (inspirationFiles.length > 0 || brandAssetsFiles.length > 0) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-yellow-800">Form Data Saved</h4>
              <p className="text-sm text-yellow-700 mt-1">
                Your form data is automatically saved as you type. However, uploaded files will need to be re-uploaded if you switch tabs or refresh the page.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Helper function to check if a question should be shown
  const shouldShowQuestion = (question: any) => {
    if (!question.showIf) return true;
    
    const { field, value } = question.showIf;
    const fieldValue = form[field];
    
    if (Array.isArray(value)) {
      return value.includes(fieldValue);
    }
    
    return fieldValue === value;
  };

  // Filter questions based on conditions
  const visibleQuestions = questions.filter(shouldShowQuestion);

  // Inspiration upload component
  const InspirationUpload: React.FC<{ name: string; description?: string }> = ({ name, description }) => {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      setInspirationFiles(prev => [...prev, ...files]);
    };

    const removeFile = (index: number) => {
      setInspirationFiles(prev => prev.filter((_, i) => i !== index));
    };

    const removeBrandAssetFile = (index: number) => {
      setBrandAssetsFiles(prev => prev.filter((_, i) => i !== index));
    };

    const getFilePreview = (file: File) => {
      if (file.type.startsWith('image/')) {
        return URL.createObjectURL(file);
      }
      return null;
    };

    return (
      <div className="space-y-8">
        {/* Upload Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center hover:border-primary transition-all duration-300 hover:bg-gray-50/50">
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.psd,.ai,.sketch"
            onChange={handleFileChange}
            className="hidden"
            id="inspiration-upload"
          />
          <label htmlFor="inspiration-upload" className="cursor-pointer block">
            <div className="mx-auto w-24 h-24 bg-gradient-to-r from-red-100 to-red-200 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-3">Upload Inspiration Files</h4>
            <p className="text-gray-600 mb-6 text-lg">Click to select multiple files or drag and drop</p>
            <div className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Choose Files
            </div>
          </label>
        </div>
        
        {/* Description */}
        {description && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-6">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-800 text-lg">{description}</p>
            </div>
          </div>
        )}
        
        {/* File Previews */}
        {inspirationFiles.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-xl font-semibold text-gray-900">Uploaded Inspiration Files</h4>
              <span className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full font-medium">
                {inspirationFiles.length} file{inspirationFiles.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {inspirationFiles.map((file, index) => (
                <div key={index} className="relative bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group">
                  {getFilePreview(file) ? (
                    <div className="mb-4">
                      <div className="relative overflow-hidden rounded-xl shadow-lg">
                        <img 
                          src={getFilePreview(file)!} 
                          alt={`Inspiration ${index + 1}`}
                          className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                        />
                        {/* Image overlay with zoom indicator */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
                          <div className="opacity-0 hover:opacity-100 transition-opacity duration-300">
                            <svg className="w-8 h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      {/* Image file indicator */}
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                        📷 Image
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 flex flex-col items-center">
                      <div className="w-20 h-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mb-3 group-hover:from-gray-200 group-hover:to-gray-300 transition-all duration-300">
                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      {/* File type indicator */}
                      <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                        📄 File
                      </div>
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <p className="text-sm font-semibold text-gray-900 truncate mb-2">{file.name}</p>
                    <p className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="absolute top-3 right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all duration-300 opacity-0 group-hover:opacity-100 shadow-lg"
                    title="Remove file"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            
            {/* Upload Summary */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h5 className="text-lg font-semibold text-green-800">Inspiration Files Ready!</h5>
                  <p className="text-green-700">
                    {inspirationFiles.filter(f => f.type.startsWith('image/')).length} image{inspirationFiles.filter(f => f.type.startsWith('image/')).length !== 1 ? 's' : ''} and {inspirationFiles.filter(f => !f.type.startsWith('image/')).length} document{inspirationFiles.filter(f => !f.type.startsWith('image/')).length !== 1 ? 's' : ''} uploaded successfully.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Brand assets upload component
  const BrandAssetsUpload: React.FC<{ name: string; description?: string }> = ({ name, description }) => {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []) as File[];
      setBrandAssetsFiles(prev => [...prev, ...files]);
    };

    const removeFile = (index: number) => {
      setBrandAssetsFiles(prev => prev.filter((_, i) => i !== index));
    };

    const getFilePreview = (file: File) => {
      if (file.type.startsWith('image/')) {
        return URL.createObjectURL(file);
      }
      return null;
    };

    return (
      <div className="space-y-8">
        {/* Upload Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center hover:border-primary transition-all duration-300 hover:bg-gray-50/50">
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.psd,.ai,.sketch,.zip,.rar"
            onChange={handleFileChange}
            className="hidden"
            id="brand-assets-upload"
          />
          <label htmlFor="brand-assets-upload" className="cursor-pointer block">
            <div className="mx-auto w-24 h-24 bg-gradient-to-r from-green-100 to-green-200 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-3">Upload Brand Assets</h4>
            <p className="text-gray-600 mb-6 text-lg">Upload your logo, brand guidelines, color palettes, and other brand elements</p>
            <div className="inline-flex items-center px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Choose Files
            </div>
          </label>
        </div>
        
        {/* Description */}
        {description && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-2xl p-6">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-green-800 text-lg">{description}</p>
            </div>
          </div>
        )}
        
        {/* File Previews */}
        {brandAssetsFiles.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-xl font-semibold text-gray-900">Uploaded Brand Assets</h4>
              <span className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full font-medium">
                {brandAssetsFiles.length} file{brandAssetsFiles.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {brandAssetsFiles.map((file, index) => (
                <div key={index} className="relative bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group">
                  {getFilePreview(file) ? (
                    <div className="mb-4">
                      <div className="relative overflow-hidden rounded-xl shadow-lg">
                        <img 
                          src={getFilePreview(file)!} 
                          alt={`Brand Asset ${index + 1}`}
                          className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                        />
                        {/* Image overlay with zoom indicator */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
                          <div className="opacity-0 hover:opacity-100 transition-opacity duration-300">
                            <svg className="w-8 h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      {/* Image file indicator */}
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                        📷 Image
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 flex flex-col items-center">
                      <div className="w-20 h-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mb-3 group-hover:from-gray-200 group-hover:to-gray-300 transition-all duration-300">
                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      {/* File type indicator */}
                      <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                        📄 File
                      </div>
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <p className="text-sm font-semibold text-gray-900 truncate mb-2">{file.name}</p>
                    <p className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="absolute top-3 right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all duration-300 opacity-0 group-hover:opacity-100 shadow-lg"
                    title="Remove file"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            
            {/* Upload Summary */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h5 className="text-lg font-semibold text-green-800">Brand Assets Ready!</h5>
                  <p className="text-green-700">
                    {brandAssetsFiles.filter(f => f.type.startsWith('image/')).length} image{brandAssetsFiles.filter(f => f.type.startsWith('image/')).length !== 1 ? 's' : ''} and {brandAssetsFiles.filter(f => !f.type.startsWith('image/')).length} document{brandAssetsFiles.filter(f => !f.type.startsWith('image/')).length !== 1 ? 's' : ''} uploaded successfully.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!user) return;
    const fetchBrands = async () => {
      setLoadingBrands(true);
      const { data, error } = await supabase
        .from('brands')
        .select('id, name')
        .eq('user_id', user.id);
      if (!error) setBrands(data || []);
      setLoadingBrands(false);
    };
    fetchBrands();
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type: inputType, checked, files } = e.target as any;
    setForm((prev) => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : inputType === 'file' ? files[0] : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);
    
    try {
      // Upload inspiration files if any
      let inspirationUrls: string[] = [];
      if (inspirationFiles.length > 0) {
        setUploadingInspirations(true);
        try {
          const uploadPromises = inspirationFiles.map(async (file, index) => {
            const fileName = `${Date.now()}_${index}_${file.name}`;
            const { data, error } = await supabase.storage
              .from('request-inspirations')
              .upload(fileName, file);
            
            if (error) throw error;
            
            const { data: { publicUrl } } = supabase.storage
              .from('request-inspirations')
              .getPublicUrl(fileName);
            
            return publicUrl;
          });
          
          inspirationUrls = await Promise.all(uploadPromises);
        } catch (uploadError) {
          console.error('Error uploading inspiration files:', uploadError);
          setError('Failed to upload some inspiration files. Please try again.');
          setUploadingInspirations(false);
          setSubmitting(false);
          return;
        } finally {
          setUploadingInspirations(false);
        }
      }

      // Upload brand assets files if any
      let brandAssetsUrls: string[] = [];
      if (brandAssetsFiles.length > 0) {
        setUploadingBrandAssets(true);
        try {
          const uploadPromises = brandAssetsFiles.map(async (file, index) => {
            const fileName = `${Date.now()}_${index}_${file.name}`;
            const { data, error } = await supabase.storage
              .from('brand-assets')
              .upload(fileName, file);
            
            if (error) throw error;
            
            const { data: { publicUrl } } = supabase.storage
              .from('brand-assets')
              .getPublicUrl(fileName);
            
            return publicUrl;
          });
          
          brandAssetsUrls = await Promise.all(uploadPromises);
        } catch (uploadError) {
          console.error('Error uploading brand assets files:', uploadError);
          setError('Failed to upload some brand assets files. Please try again.');
          setUploadingBrandAssets(false);
          setSubmitting(false);
          return;
        } finally {
          setUploadingBrandAssets(false);
        }
      }

      // Prepare answers, excluding brand_id
      const { brand_id, ...rawAnswers } = form;
      
      // Add inspiration URLs to answers
      if (inspirationUrls.length > 0) {
        rawAnswers.inspirations = inspirationUrls;
      }
      
      // Create structured questions and answers
      const questionsAndAnswers = visibleQuestions.map(question => ({
        question: question.label,
        answer: question.type === 'inspiration_upload' ? inspirationUrls : 
                question.type === 'brand_assets_upload' ? brandAssetsUrls : 
                (rawAnswers[question.name] || ''),
        fieldName: question.name,
        fieldType: question.type || 'text',
        required: question.required || false
      }));

      const { error: insertError } = await supabase.from('service_requests').insert([
        {
          user_id: user?.id,
          brand_id: brand_id,
          type,
          status: 'pending', // Explicitly set status to match kanban board expectations
          answers: rawAnswers, // Keep original format for backward compatibility
          questions_and_answers: questionsAndAnswers, // New structured format
        },
      ]);
      if (insertError) {
        setError('Failed to submit request: ' + insertError.message);
      } else {
        // Track request creation
        trackRequestCreated(type || 'unknown', {
          user_id: user?.id,
          brand_id: brand_id,
          questions_count: visibleQuestions.length,
          has_brand: !!brand_id,
          has_inspirations: inspirationUrls.length > 0
        });
        
        setSuccess(true);
        setForm({});
        setInspirationFiles([]);
        setBrandAssetsFiles([]);
        clearSavedForm(); // Clear saved form data after successful submission
      }
    } catch (error: any) {
      setError('Failed to submit request: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!type || !visibleQuestions.length) {
    return <div className="text-gray-500">Invalid request type.</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-primary to-primary-dark rounded-2xl mb-6 shadow-xl">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{typeTitles[type]}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tell us about your project and share your vision. We'll use this information to create exactly what you need.
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-green-900 mb-2">Request Submitted Successfully!</h3>
            <p className="text-green-700 text-lg">We've received your request and will get back to you soon.</p>
          </div>
        )}

        {/* Main Form */}
        {!success && (
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            {/* Form Header */}
            <div className="bg-gradient-to-r from-primary via-primary-dark to-primary px-8 py-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Project Details</h2>
                  <p className="text-red-100">Fill out the form below to get started</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-white rounded-full opacity-60 animate-pulse"></div>
                  <div className="w-3 h-3 bg-white rounded-full opacity-60 animate-pulse" style={{animationDelay: '0.2s'}}></div>
                  <div className="w-3 h-3 bg-white rounded-full opacity-60 animate-pulse" style={{animationDelay: '0.4s'}}></div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-10">
              {/* Brand Selection */}
              <div className="bg-gradient-to-r from-gray-50 to-red-50 rounded-2xl p-8 border border-gray-200">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-100 to-red-200 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <label className="block text-xl font-semibold text-gray-900 mb-2">Select Your Brand</label>
                    <p className="text-gray-600">Choose the brand this project is for</p>
                  </div>
                </div>
                <select
                  name="brand_id"
                  value={form.brand_id || ''}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg text-gray-900 bg-white shadow-sm"
                  disabled={loadingBrands}
                >
                  <option value="" disabled>{loadingBrands ? 'Loading brands...' : 'Select a brand'}</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>

              {/* Form Data Persistence Warning */}
              {showFileWarning()}

              {/* Manual Save Button */}
              {Object.keys(form).length > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-blue-800">Auto-Save Enabled</h4>
                        <p className="text-blue-700">Your form data is automatically saved as you type</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        localStorage.setItem(formStorageKey, JSON.stringify(form));
                        console.log('💾 Manual save completed');
                      }}
                      className="px-6 py-3 bg-blue-100 text-blue-700 rounded-xl text-sm font-semibold hover:bg-blue-200 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      Save Now
                    </button>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-red-800">Submission Error</h4>
                      <p className="text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Questions Section */}
              <div className="space-y-10">
                <div className="text-center mb-10">
                  <div className="flex items-center justify-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900">Project Requirements</h3>
                      <p className="text-xl text-gray-600">Answer these questions to help us understand your vision</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {visibleQuestions.map((q, index) => (
                    <div key={q.name} className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                      <div className="flex items-start space-x-6">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-primary to-primary-dark text-white rounded-2xl flex items-center justify-center font-bold text-lg shadow-lg">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <label className="block text-xl font-semibold text-gray-900 mb-4">
                            {q.label}
                            {q.required && <span className="text-red-500 ml-2">*</span>}
                          </label>
                          
                          {q.type === 'textarea' ? (
                            <textarea
                              name={q.name}
                              required={q.required}
                              value={form[q.name] || ''}
                              onChange={handleChange}
                              rows={4}
                              className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg text-gray-900 placeholder-gray-400 resize-none"
                              placeholder="Type your answer here..."
                            />
                          ) : q.type === 'select' ? (
                            <select
                              name={q.name}
                              required={q.required}
                              value={form[q.name] || ''}
                              onChange={handleChange}
                              className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg text-gray-900 bg-white"
                            >
                              <option value="" disabled>Select an option</option>
                              {q.options?.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : q.type === 'checkbox' ? (
                            <div className="flex items-center space-x-4">
                              <input
                                type="checkbox"
                                name={q.name}
                                checked={!!form[q.name]}
                                onChange={handleChange}
                                className="w-6 h-6 text-primary border-gray-300 rounded-xl focus:ring-primary focus:ring-4"
                              />
                              <span className="text-lg text-gray-700">Yes, I agree</span>
                            </div>
                          ) : q.type === 'file' ? (
                            <div className="space-y-6">
                              {/* Upload Area */}
                              <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-primary transition-all duration-300 hover:bg-gray-50/50">
                                <input
                                  type="file"
                                  name={q.name}
                                  onChange={handleChange}
                                  accept="image/*,.pdf,.doc,.docx,.psd,.ai,.sketch"
                                  className="hidden"
                                  id={`file-${q.name}`}
                                />
                                <label htmlFor={`file-${q.name}`} className="cursor-pointer block">
                                  <div className="mx-auto w-20 h-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mb-6">
                                    <svg className="w-10 h-10 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </div>
                                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Click to upload file</h4>
                                  <p className="text-gray-600">or drag and drop</p>
                                </label>
                              </div>
                              
                              {/* File Preview */}
                              {form[q.name] && (
                                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                                  <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-lg font-semibold text-gray-900">Uploaded File</h4>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setForm(prev => ({ ...prev, [q.name]: null }));
                                      }}
                                      className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all duration-300"
                                      title="Remove file"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                  
                                  <div className="flex items-center space-x-4">
                                    {/* Image Preview */}
                                    {form[q.name].type && form[q.name].type.startsWith('image/') ? (
                                      <div className="relative overflow-hidden rounded-xl shadow-lg">
                                        <img 
                                          src={URL.createObjectURL(form[q.name])} 
                                          alt="Uploaded file preview"
                                          className="w-24 h-24 object-cover"
                                        />
                                        {/* Image indicator */}
                                        <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                                          📷
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="w-24 h-24 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        {/* File type indicator */}
                                        <div className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                                          📄
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* File Info */}
                                    <div className="flex-1">
                                      <p className="text-sm font-semibold text-gray-900 truncate mb-1">
                                        {form[q.name].name}
                                      </p>
                                      <p className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                                        {(form[q.name].size / 1024 / 1024).toFixed(2)} MB
                                      </p>
                                      <p className="text-xs text-gray-400 mt-1">
                                        {form[q.name].type || 'Unknown type'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : q.type === 'inspiration_upload' ? (
                            <InspirationUpload name={q.name} description={q.description} />
                          ) : q.type === 'brand_assets_upload' ? (
                            <BrandAssetsUpload name={q.name} description={q.description} />
                          ) : (
                            <input
                              type={q.type || 'text'}
                              name={q.name}
                              required={q.required}
                              value={form[q.name] || ''}
                              onChange={handleChange}
                              className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg text-gray-900 placeholder-gray-400"
                              placeholder={`Enter your ${q.label.toLowerCase()}...`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-8">
                <button
                  type="submit"
                  disabled={submitting || uploadingInspirations || uploadingBrandAssets}
                  className="w-full bg-gradient-to-r from-primary to-primary-dark text-white px-8 py-6 rounded-2xl font-bold text-xl hover:from-primary-dark hover:to-primary transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                >
                  {uploadingInspirations ? (
                    <div className="flex items-center justify-center space-x-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      <span>Uploading Inspirations...</span>
                    </div>
                  ) : uploadingBrandAssets ? (
                    <div className="flex items-center justify-center space-x-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      <span>Uploading Brand Assets...</span>
                    </div>
                  ) : submitting ? (
                    <div className="flex items-center justify-center space-x-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      <span>Submitting Request...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-3">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Submit Request</span>
                    </div>
                  )}
                </button>
                
                <p className="text-center text-gray-500 mt-6 text-lg">
                  By submitting this request, you agree to our terms of service and privacy policy.
                </p>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestForm; 